// DTOs validate and shape data at the application boundary
// (between UI and services, or services and adapters).

import { sanitizeBuyerNote } from '../rules/playBuyerNote';
import { sanitizePayoutMultipliersPatch } from '../rules/payoutMultipliers';
import { resolveWindowSchedule } from '../rules/windowSchedule';
import {
  nativeOfficialPicksBag,
  resolveOfficialReading,
} from '../lottery/officialDrawFormat';

export type CreatePlayDto = {
  type: string;
  number: string;
  number2?: string;
  stake: number;
  houseId: string;
  listerId: string;
  listerName: string;
  window: string;
  clientId?: string;
  buyerNote?: string;
  actor?: Record<string, unknown>;
};

/** Build and coerce a CreatePlayDto from form values. */
export function buildCreatePlayDto(raw: Record<string, unknown>): CreatePlayDto {
  const dto: CreatePlayDto = {
    type:       String(raw.type ?? '').toLowerCase(),
    number:     String(raw.number ?? '').trim(),
    number2:    raw.number2 ? String(raw.number2).trim() : undefined,
    stake:      Math.abs(Number(raw.stake ?? 0)),
    houseId:    String(raw.houseId ?? ''),
    listerId:   String(raw.listerId ?? ''),
    listerName: String(raw.listerName ?? ''),
    window:     String(raw.window ?? 'day'),
    clientId:   raw.clientId ? String(raw.clientId).slice(0, 120) : undefined,
  };
  const buyerNote = sanitizeBuyerNote(raw.buyerNote);
  if (buyerNote) dto.buyerNote = buyerNote;
  return dto;
}

export type PublishResultDto = {
  houseId: string;
  window: string;
  first: string;
  second: string;
  third: string;
  source: string;
  dayKey?: string;
  feed?: string;
  publishedBy?: string;
  publishedByName?: string;
  /** Optional lottery snapshot — field names follow the house source style. */
  picks?: {
    pick2?: string | null;
    pick3?: string | null;
    pick4?: string | null;
    pick5?: string | null;
    numbers?: string | null;
    win4?: string | null;
    cash3?: string | null;
    cash4?: string | null;
  };
};

export function buildPublishResultDto(raw: Record<string, unknown>): PublishResultDto {
  const firstDigits = String(raw.first ?? '').replace(/\D/g, '');
  const pick3FromFirst = firstDigits.length >= 3
    ? firstDigits.padStart(3, '0').slice(-3)
    : undefined;
  const first = firstDigits.padStart(2, '0').slice(-2);
  const second = String(raw.second ?? '').replace(/\D/g, '').padStart(2, '0').slice(-2);
  const third = String(raw.third ?? '').replace(/\D/g, '').padStart(2, '0').slice(-2);
  const source = String(raw.source ?? 'florida');
  const picksRaw = raw.picks && typeof raw.picks === 'object'
    ? raw.picks as Record<string, unknown>
    : null;
  const resolved = resolveOfficialReading(source, {
    first,
    second,
    third,
    pick3: picksRaw?.pick3 != null ? String(picksRaw.pick3) : pick3FromFirst,
    pick4: picksRaw?.pick4 != null ? String(picksRaw.pick4) : null,
    numbers: picksRaw?.numbers != null ? String(picksRaw.numbers) : null,
    win4: picksRaw?.win4 != null ? String(picksRaw.win4) : null,
    cash3: picksRaw?.cash3 != null ? String(picksRaw.cash3) : null,
    cash4: picksRaw?.cash4 != null ? String(picksRaw.cash4) : null,
  });
  const dto: PublishResultDto = {
    houseId: String(raw.houseId ?? ''),
    window: String(raw.window ?? 'day'),
    first,
    second,
    third,
    source,
  };
  if (resolved.three || resolved.four) {
    dto.picks = nativeOfficialPicksBag(source, resolved.three, resolved.four, {
      pick2: picksRaw?.pick2 != null ? String(picksRaw.pick2) : null,
      pick5: picksRaw?.pick5 != null ? String(picksRaw.pick5) : null,
    }) as PublishResultDto['picks'];
  }
  return dto;
}

export type UpdateConfigDto = {
  dayOpen: string;
  dayClose: string;
  nightOpen: string;
  nightClose: string;
  cutoffMin: number;
  perNumberCap: number;
  source: string;
  numberCaps?: Record<string, number>;
  surgePlayThreshold?: number;
  surgeCapRatio?: number;
  autoFloridaSync?: boolean;
  autoNewYorkSync?: boolean;
  routeDepartMode?: 'manual' | 'scheduled';
  routeDepartAt?: string;
  routeDepartMarginMin?: number;
  routeIdleAlertMin?: number;
  runnerMaxLoadAmount?: number;
  payoutMultipliers?: {
    fijo?: number;
    corrido?: number;
    parle?: number;
    centena?: number;
  };
};

export function buildUpdateConfigDto(raw: Record<string, unknown>): UpdateConfigDto {
  const resolved = resolveWindowSchedule(raw);
  const dto: UpdateConfigDto = {
    dayOpen:      String(raw.dayOpen ?? resolved.dayOpen),
    dayClose:     String(raw.dayClose ?? resolved.dayClose),
    nightOpen:    String(raw.nightOpen ?? resolved.nightOpen),
    nightClose:   String(raw.nightClose ?? resolved.nightClose),
    cutoffMin:    Number(raw.cutoffMin ?? resolved.cutoffMin),
    perNumberCap: Number(raw.perNumberCap ?? 5000),
    source:       String(raw.source ?? 'florida'),
  };
  if (raw.numberCaps && typeof raw.numberCaps === 'object') {
    dto.numberCaps = raw.numberCaps as Record<string, number>;
  }
  if (raw.surgePlayThreshold != null) dto.surgePlayThreshold = Number(raw.surgePlayThreshold);
  if (raw.surgeCapRatio != null) dto.surgeCapRatio = Number(raw.surgeCapRatio);
  if (typeof raw.autoFloridaSync === 'boolean') dto.autoFloridaSync = raw.autoFloridaSync;
  if (typeof raw.autoNewYorkSync === 'boolean') dto.autoNewYorkSync = raw.autoNewYorkSync;
  if (raw.routeDepartMode === 'manual' || raw.routeDepartMode === 'scheduled') {
    dto.routeDepartMode = raw.routeDepartMode;
  }
  if (raw.routeDepartAt != null) dto.routeDepartAt = String(raw.routeDepartAt);
  if (raw.routeDepartMarginMin != null) {
    dto.routeDepartMarginMin = Math.max(0, Number(raw.routeDepartMarginMin));
  }
  if (raw.routeIdleAlertMin != null) {
    dto.routeIdleAlertMin = Math.max(1, Math.min(120, Number(raw.routeIdleAlertMin)));
  }
  if (raw.runnerMaxLoadAmount != null) {
    const n = Math.floor(Number(raw.runnerMaxLoadAmount));
    if (Number.isFinite(n) && n > 0) dto.runnerMaxLoadAmount = n;
  }
  const payoutMultipliers = sanitizePayoutMultipliersPatch(raw.payoutMultipliers);
  if (payoutMultipliers) dto.payoutMultipliers = payoutMultipliers;
  return dto;
}

export type RegisterStaffDto = {
  name: string;
  alias?: string;
  email: string;
  role: string;
  houseId: string;
  houseIds?: string[];
  commission: number;
  paymentMode: string;
  status: string;
  offerGoogleLinkOnboarding?: boolean;
  compensationType?: string | null;
  fixedFee?: number;
  agreementEnabled?: boolean;
  /** Optional Cuban identity number (digits). */
  ciNumber?: string;
  /** Optional street address for locating the staff member. */
  addr?: string;
  bio?: string;
  /** Private note visible to banker / god. */
  bankerNote?: string;
};

export function buildRegisterStaffDto(raw: Record<string, unknown>): RegisterStaffDto {
  const dto: RegisterStaffDto = {
    name: String(raw.name ?? '').trim(),
    email: String(raw.email ?? '').trim().toLowerCase(),
    role: String(raw.role ?? '').toLowerCase(),
    houseId: String(raw.houseId ?? ''),
    houseIds: Array.isArray(raw.houseIds) ? raw.houseIds.map(String) : undefined,
    commission: Number(raw.commission ?? 0),
    paymentMode: String(raw.paymentMode ?? 'cash'),
    status: 'active',
  };
  const alias = String(raw.alias ?? '').trim();
  if (alias) dto.alias = alias;
  if (typeof raw.offerGoogleLinkOnboarding === 'boolean') {
    dto.offerGoogleLinkOnboarding = raw.offerGoogleLinkOnboarding;
  }
  if (raw.compensationType === null) dto.compensationType = null;
  else if (raw.compensationType === 'fixed' || raw.compensationType === 'percent') {
    dto.compensationType = raw.compensationType;
  }
  if (raw.fixedFee != null) dto.fixedFee = Number(raw.fixedFee);
  if (typeof raw.agreementEnabled === 'boolean') dto.agreementEnabled = raw.agreementEnabled;

  const ciNumber = String(raw.ciNumber ?? '').replace(/\D/g, '').slice(0, 11);
  if (ciNumber) dto.ciNumber = ciNumber;
  const addr = String(raw.addr ?? raw.address ?? '').trim();
  if (addr) dto.addr = addr;
  const bio = String(raw.bio ?? '').trim();
  if (bio) dto.bio = bio.slice(0, 500);
  const bankerNote = String(raw.bankerNote ?? '').trim();
  if (bankerNote) dto.bankerNote = bankerNote.slice(0, 1000);
  return dto;
}

export type StaffProfilePatch = {
  name?: string;
  alias?: string;
  email?: string;
  role?: string;
  houseId?: string;
  houseIds?: string[];
  addr?: string;
  bio?: string;
  bankerNote?: string;
  ciNumber?: string;
  commission?: number;
  paymentMode?: string;
  compensationType?: string | null;
  fixedFee?: number | null;
  agreementEnabled?: boolean;
  profilePhotoUrl?: string;
  ciFrontUrl?: string;
  ciBackUrl?: string;
};

const STAFF_AUTH_SYNC_KEYS = new Set([
  'email', 'role', 'houseId', 'houseIds', 'name', 'alias',
  'addr', 'bio', 'bankerNote', 'ciNumber',
]);

/** True when the patch needs Admin Auth / claims sync (god callable). */
export function staffPatchNeedsAuthSync(patch: Record<string, unknown>): boolean {
  return Object.keys(patch).some((key) => STAFF_AUTH_SYNC_KEYS.has(key));
}

export function buildStaffProfilePatch(raw: Record<string, unknown>): StaffProfilePatch {
  const patch: StaffProfilePatch = {};
  if (raw.name != null) patch.name = String(raw.name).trim();
  if (raw.alias !== undefined) patch.alias = String(raw.alias ?? '').trim();
  if (raw.email != null) patch.email = String(raw.email).trim().toLowerCase();
  if (raw.role != null) patch.role = String(raw.role);
  if (raw.houseId != null) patch.houseId = String(raw.houseId).trim();
  if (Array.isArray(raw.houseIds)) patch.houseIds = raw.houseIds.map(String).filter(Boolean);
  if (raw.addr !== undefined) patch.addr = String(raw.addr ?? '').trim();
  if (raw.bio !== undefined) patch.bio = String(raw.bio ?? '').trim();
  if (raw.bankerNote !== undefined) patch.bankerNote = String(raw.bankerNote ?? '').trim();
  if (raw.ciNumber !== undefined) patch.ciNumber = String(raw.ciNumber ?? '').replace(/\D/g, '');
  if (raw.commission != null) patch.commission = Number(raw.commission);
  if (raw.paymentMode != null) patch.paymentMode = String(raw.paymentMode);
  if (raw.compensationType === null) patch.compensationType = null;
  else if (raw.compensationType === 'fixed' || raw.compensationType === 'percent') {
    patch.compensationType = raw.compensationType;
  }
  if (raw.fixedFee !== undefined) {
    patch.fixedFee = raw.fixedFee == null ? null : Number(raw.fixedFee);
  }
  if (typeof raw.agreementEnabled === 'boolean') patch.agreementEnabled = raw.agreementEnabled;
  if (raw.profilePhotoUrl != null) patch.profilePhotoUrl = String(raw.profilePhotoUrl);
  if (raw.ciFrontUrl != null) patch.ciFrontUrl = String(raw.ciFrontUrl);
  if (raw.ciBackUrl != null) patch.ciBackUrl = String(raw.ciBackUrl);
  return patch;
}
